/* eslint-disable react/prop-types */
/**
 * StatusHistoryTab — append-only deal_status_history rows.
 *
 * Backend supports POST + DELETE but NOT PUT. We surface the same
 * WigMetadataCrudPanel with `appendOnly`, which suppresses the edit
 * modal on row click. Rows are server-ordered DESC by `changed_at` (newest
 * first) — we re-sort defensively here in case the API ever returns rows
 * out of order.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Card, Space } from "antd";

import { WigMetadataCrudPanel } from "@warwick-investment-group/wig-react-library";

import { AppContext } from "../../../AppContextProvider";
import dealsService from "../../../services/dealsService";
import { apiErrorText } from "../../../utils/apiError";

// Strip from metadata before passing — the library panel has no `hideFields`
// prop, so pre-filtering is the supported way to suppress grid + form rows.
const HIDDEN_FIELDS = new Set(["deal_id"]);

const StatusHistoryTab = ({ dealId }) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);

  const [rows, setRows] = useState([]);
  const [metadata, setMetadata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState(null);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const refresh = useCallback(async () => {
    if (!dealId) return;
    try {
      setLoading(true);
      setErrMsg(null);
      const { token: tk, userOid } = await getAuth();
      const [listResp, metaResp] = await Promise.all([
        dealsService.listStatusHistory({ token: tk, userOid, dealId }),
        dealsService.getMetadata({ token: tk, userOid, tableName: "deal_status_history" }),
      ]);
      setRows(listResp?.data?.data || []);
      setMetadata(metaResp?.data?.data || []);
    } catch (err) {
      console.error("StatusHistoryTab load failed:", err);
      setErrMsg(apiErrorText(err));
    } finally {
      setLoading(false);
    }
  }, [dealId, getAuth]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const sortedRows = useMemo(() => {
    const out = [...rows];
    out.sort((a, b) =>
      String(b.changed_at || "").localeCompare(String(a.changed_at || ""))
    );
    return out;
  }, [rows]);

  const filteredMetadata = useMemo(
    () => (metadata || []).filter((m) => !HIDDEN_FIELDS.has(m.field)),
    [metadata]
  );

  // Status history is a read-only audit trail — the backend writes a row
  // automatically whenever a deal's status changes. We deliberately pass
  // NO onCreate/onUpdate/onDelete so the panel renders no Add button, no
  // row-click edit, and no delete column (per UAT: the history must not be
  // user-editable). The backend still exposes POST/DELETE, but the UI does
  // not surface them.
  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {errMsg && <Alert type="error" showIcon message="Failed to load status history" description={errMsg} />}
      <Card size="small" title={`Status history (${sortedRows.length})`}>
        <WigMetadataCrudPanel
          title="Newest first — read only"
          rows={sortedRows}
          metadata={filteredMetadata}
          loading={loading}
          allowView
          disableNonEditable
          emptyText="No status changes recorded yet"
          height={420}
        />
      </Card>
    </Space>
  );
};

export default StatusHistoryTab;
